import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ICONS = {
  '/':            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  '/ask':         <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  '/resume':      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  '/assessment':  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  '/sprint':      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  '/mock-interview': <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  '/feedback':    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  '/account':     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  '/admin':       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
};

const NAV_LINKS = [
  { to: '/',            label: 'Dashboard',        desc: 'Home' },
  { to: '/ask',         label: 'Ask Nitin',        desc: 'AI Legal Career Advisor' },
  { to: '/sprint',      label: 'Sprint',            desc: 'Mentorship programme' },
  { to: '/assessment',  label: 'Find Your Practice Area',   desc: 'Practice area assessment' },
  { to: '/resume',      label: 'Resume Builder and Assessment',           desc: 'Build your resume and get assessed' },
  { to: '/mock-interview', label: 'Mock Interview', desc: 'Interview prep with Nitin' },
  { to: '/feedback',    label: 'Feedback',          desc: 'Share your feedback' },
  { to: '/about',       label: 'About',            desc: 'About Ask Nitin' },
  { to: '/account',     label: 'Account',          desc: 'Profile & settings' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/login');
  };

  const links = [
    ...NAV_LINKS,
    ...((['admin', 'superadmin'].includes(user?.role))
      ? [{ to: '/admin', label: 'Admin Panel', desc: 'Manage platform' }]
      : []),
  ];

  return (
    <>
      <header style={s.topBar}>
        <div style={s.topInner}>
          <button style={s.menuBtn} onClick={() => setOpen(true)} aria-label="Open menu">
            <span style={s.menuLine} /><span style={s.menuLine} /><span style={s.menuLine} />
          </button>
          <Link to="/" style={s.brand}>
            <img src="/Ask Nitin Brand.png" alt="Ask Nitin" style={s.brandImg} />
            <span style={s.brandTagline}>Personal Mentor<br/>for Law Students</span>
          </Link>
          <div style={s.userAva}>
            {(user?.name?.[0] || user?.phone?.slice(-1) || 'U').toUpperCase()}
          </div>
        </div>
      </header>

      {location.pathname !== '/' && (
        <div style={s.backBar}>
          <button onClick={() => navigate(-1)} style={s.backBtn}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Back
          </button>
        </div>
      )}

      {open && <div style={s.backdrop} onClick={() => setOpen(false)} />}

      <aside style={{ ...s.drawer, transform: open ? 'translateX(0)' : 'translateX(-100%)' }}>
        <div style={s.drawerHeader}>
          <img src="/NitinPotdarAskNitinBanner.png" alt="Nitin Potdar" style={s.drawerHeroImg} />
          <button style={s.closeBtn} onClick={() => setOpen(false)}>✕</button>
        </div>
        <div style={s.drawerBrand}>
          <img src="/Ask Nitin Brand.png" alt="Ask Nitin" style={s.drawerBrandImg} />
          <span style={s.drawerBrandTagline}>Personal Mentor<br/>for Law Students</span>
        </div>

        <div style={s.drawerUser}>
          <div style={s.drawerAva}>{(user?.name?.[0] || 'U').toUpperCase()}</div>
          <div style={s.drawerUserInfo}>
            <p style={s.drawerUserName}>{user?.name || 'Student'}</p>
            <p style={s.drawerUserPhone}>{user?.phone}</p>
          </div>
          <span style={s.rolePill}>{user?.role}</span>
        </div>

        <div style={s.divider} />

        <nav style={s.navList}>
          <p className="section-cap" style={{ padding: '10px 16px 6px' }}>Navigation</p>
          {links.map(({ to, label, desc }) => (
            <Link key={to} to={to} style={{ ...s.navItem, ...(isActive(to) ? s.navItemActive : {}) }}>
              <span style={{ ...s.navIcon, ...(isActive(to) ? s.navIconActive : {}) }}>
                {ICONS[to] || ICONS['/admin']}
              </span>
              <div style={s.navText}>
                <span style={{ ...s.navLabel, ...(isActive(to) ? s.navLabelActive : {}) }}>{label}</span>
                <span style={s.navDesc}>{desc}</span>
              </div>
              {isActive(to) && <div style={s.activeLine} />}
            </Link>
          ))}
        </nav>

        <div style={{ flex: 1 }} />
        <div style={s.divider} />

        <button onClick={handleLogout} style={s.logoutBtn}>
          <span style={s.logoutIcon}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          <span style={s.logoutLabel}>Sign Out</span>
        </button>
        <div style={{ height: '20px' }} />
      </aside>
    </>
  );
}

const s = {
  topBar: {
    position: 'sticky', top: 0, zIndex: 900, height: '60px',
    background: 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid var(--border-2)',
    boxShadow: '0 1px 6px rgba(12,35,64,0.05)',
  },
  backBar: {
    maxWidth: '1200px', margin: '0 auto', padding: '6px 16px',
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '4px',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600', color: 'var(--text-2)',
    padding: '4px 0', fontFamily: "'DM Sans', sans-serif",
  },
  topInner: {
    maxWidth: '1200px', margin: '0 auto', padding: '0 16px',
    height: '100%', display: 'flex', alignItems: 'center', gap: '12px',
  },
  menuBtn: {
    width: '36px', height: '36px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '4px',
    background: 'transparent', border: '1px solid var(--border)',
    borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
  },
  menuLine: { display: 'block', width: '15px', height: '1.5px', background: 'var(--navy)', borderRadius: '2px' },
  brand: {
    textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: '8px',
    flex: 1, justifyContent: 'center',
  },
  brandImg: { height: '46px', objectFit: 'contain' },
  brandTagline: { fontSize: '9px', color: 'var(--text-2)', fontWeight: '600', lineHeight: '1.4', whiteSpace: 'nowrap' },
  userAva: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: 'var(--navy)', color: '#fff', fontWeight: '600', fontSize: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1.5px solid var(--gold-mid)', flexShrink: 0,
  },
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(12,35,64,0.45)',
    backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', cursor: 'pointer',
  },
  drawer: {
    position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px',
    zIndex: 1100, background: '#fff',
    boxShadow: '4px 0 32px rgba(12,35,64,0.14)',
    display: 'flex', flexDirection: 'column',
    transition: 'transform 0.26s cubic-bezier(0.4,0,0.2,1)',
    borderRight: '1px solid var(--border)',
    overflowY: 'auto',
  },
  drawerHeader: {
    position: 'relative', flexShrink: 0, overflow: 'hidden',
  },
  drawerHeroImg: {
    width: '100%', display: 'block', objectFit: 'cover',
    objectPosition: 'top',
  },
  drawerBrand: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '14px 16px', background: 'var(--navy)', flexShrink: 0,
  },
  drawerBrandImg: { height: '32px', objectFit: 'contain' },
  drawerBrandTagline: { fontSize: '10px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.4', whiteSpace: 'nowrap' },
  closeBtn: {
    position: 'absolute', top: '10px', right: '10px',
    width: '28px', height: '28px', borderRadius: '50%',
    background: 'rgba(0,0,0,0.35)', border: 'none',
    color: '#fff', fontSize: '12px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2,
  },
  drawerUser: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', background: 'var(--bg)', flexShrink: 0,
  },
  drawerAva: {
    width: '38px', height: '38px', borderRadius: '50%',
    background: 'var(--navy)', color: '#fff', fontWeight: '700', fontSize: '15px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, border: '1.5px solid var(--gold-mid)',
  },
  drawerUserInfo: { flex: 1, minWidth: 0 },
  drawerUserName: {
    fontSize: '13px', fontWeight: '600', color: 'var(--text)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  drawerUserPhone: { fontSize: '11px', color: 'var(--text-3)', marginTop: '1px' },
  rolePill: {
    background: 'var(--gold-light)', color: 'var(--gold)',
    fontSize: '9px', fontWeight: '700', padding: '2px 7px',
    borderRadius: '99px', textTransform: 'capitalize',
    border: '1px solid var(--gold-mid)', flexShrink: 0,
  },
  divider: { height: '1px', background: 'var(--border-2)' },
  navList: { padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: '1px' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '9px 10px', borderRadius: '10px',
    textDecoration: 'none', position: 'relative',
    transition: 'background 0.12s',
  },
  navItemActive: { background: 'var(--gold-light)' },
  navIcon: {
    width: '32px', height: '32px', borderRadius: '8px',
    background: 'var(--bg)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: 'var(--text-3)', flexShrink: 0,
  },
  navIconActive: { background: 'var(--navy)', color: '#fff' },
  navText: { display: 'flex', flexDirection: 'column', gap: '0px', flex: 1 },
  navLabel: { fontSize: '13px', fontWeight: '600', color: 'var(--text-2)' },
  navLabelActive: { color: 'var(--navy)', fontWeight: '700' },
  navDesc: { fontSize: '10px', color: 'var(--text-3)' },
  activeLine: { width: '3px', height: '18px', borderRadius: '2px', background: 'var(--gold)' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 18px', background: 'none', border: 'none',
    cursor: 'pointer', width: '100%',
  },
  logoutIcon: {
    width: '32px', height: '32px', borderRadius: '8px',
    background: 'var(--danger-bg)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  logoutLabel: { fontSize: '13px', fontWeight: '600', color: 'var(--danger)' },
};
